import React, { useCallback, useState } from 'react'
import { get, find, pick } from 'lodash-es'
import { ErrorBoundary } from 'react-error-boundary'
import { ModalsProvider } from '@mantine/modals'
import {
  TbViewProvider,
  useTbViewStore,
  useMatrix,
  useFetchRemoteData,
  useBeforeTransform,
  useProjectStore,
} from '@vt/hooks'
import { DittoProvider } from '@ow/ditto-core'
import { Box } from '@ow/comps'
import { upload, addWatermarkToImage } from '@vt/apis/lib-ex/v2/givrali'
import { IconMaps } from '@ow/icons'
import styled from '@emotion/styled'
import { ResizableBox } from 'react-resizable'
import NavMenu from '../../container/container-render/NavMenuOperate'
import ContainerRender from '../../container/container-render'
import Standard from './TreeFilters'
import DataOperateProvider from '../../data-operate/DataOperateProvider'

const showDataTypes = ['list', 'calendar', 'article', 'file', 'map']

const StyledResizableBox = styled(ResizableBox)`
  position: relative;

  .react-resizable-handle {
    position: absolute;
    width: 10px;
    height: 100%;
    right: 0;
    top: 0;
    cursor: e-resize;
    z-index: 1;
  }
`

const ViewMatrixRender = function () {
  const [expand, setExpand] = useState(true)
  const [width, setWidth] = useState(256)
  const { standardModel, areaModel } = useMatrix()
  const { layouts = {} } = useTbViewStore((state) => pick(state, ['layouts']))

  const dataElement = find(
    get((layouts && layouts.layouts) || layouts, 'elements', []),
    (element) => {
      return (
        element.type === 'data' ||
        (element.elements &&
          element.elements.find((v) => showDataTypes.includes(v.type)))
      )
    }
  )

  const onResize = (event, { size }) => {
    setWidth(size.width)
  }

  return (
    <DataOperateProvider>
      <div className="h-full flex flex-row overflow-hidden flex-1">
        <StyledResizableBox
          width={width}
          height={Infinity}
          minConstraints={[200, Infinity]}
          maxConstraints={[500, Infinity]}
          onResize={onResize}
          axis="x"
          className="customResizable"
        >
          <div
            className="flex-col h-full bg-white px-3 text-sm"
            style={{ display: expand ? 'flex' : 'none' }}
          >
            <div
              className="flex items-end justify-end"
              onClick={() => {
                setExpand(false)
                setWidth(0)
              }}
            >
              <IconMaps type="contract" />
            </div>
            <Standard
              standardModel={standardModel}
              areaModel={areaModel}
              expand={expand}
            />
          </div>
        </StyledResizableBox>

        <div
          className="h-full relative flex flex-row overflow-hidden flex-1"
          style={{ margin: '14px' }}
        >
          {!expand && (
            <div
              style={{
                width: '24px',
                height: '24px',
                border: '2px solid #DCDCDC',
                borderRadius: '4px',
              }}
              className="flex items-center justify-center bg-white"
              onClick={() => {
                setExpand(true)
                setWidth(256)
              }}
            >
              <IconMaps type="expand" width={16} height={16} />
            </div>
          )}
          <NavMenu
            layouts={get(
              (layouts && layouts.layouts) || layouts,
              'navMenu',
              []
            )}
          />
          <div className="flex gap-2 h-full flex-1">
            <div className="flex flex-col gap-2 h-full flex-1">
              <ErrorBoundary fallback={<div>layouts渲染错误</div>}>
                <Box
                  className="flex-1 flex"
                  style={{
                    border: 'none',
                  }}
                >
                  <ContainerRender layouts={dataElement} />
                </Box>
              </ErrorBoundary>
            </div>
          </div>
        </div>
      </div>
    </DataOperateProvider>
  )
}

const TbViewMatrix = function ({
  appId,
  tbId,
  vid,
  matrixOperateRefs, // 用于定制操作，比如查看统计、申诉等等
  matrixCustomFields, // 用于定制列，比如查看统计、申诉等等
  mergeStandardIds,
  children,
}) {
  const fetchRemoteData = useFetchRemoteData()
  const { customCompsMap } = useProjectStore((state) =>
    pick(state, ['customCompsMap'])
  )
  const beforeTransform =
    customCompsMap && customCompsMap.useBeforeTransform
      ? customCompsMap.useBeforeTransform
      : useBeforeTransform

  const extendsStore = useCallback((set) => {
    function extend() {
      const setMatrix = (
        matrixTbData,
        matrixColumns,
        matrixCellInfo,
        columnShowType,
        matrixMaxLevel
      ) => {
        set({
          matrixTbData: matrixTbData || [],
          matrixColumns: matrixColumns || [],
          matrixCellInfo,
          columnShowType,
          matrixMaxLevel,
        })
      }

      const setMatrixSelected = (matrixSelected) => {
        set({ matrixSelected })
      }

      const setMatrixInited = (matrixInited) => {
        set({ matrixInited })
      }

      return {
        matrixInited: false,
        matrixTbData: [],
        matrixColumns: [],
        matrixCellInfo: null,
        matrixOperateRefs,
        matrixCustomFields,
        mergeStandardIds,
        setMatrixSelected,
        setMatrixInited,
        setMatrix,
      }
    }

    return extend()
  }, [])

  return (
    <DittoProvider
      tbId={tbId}
      onFetchRemoteData={fetchRemoteData}
      upload={upload}
      addWatermarkToImage={addWatermarkToImage}
      beforeTransform={beforeTransform}
    >
      <TbViewProvider
        appId={appId}
        tbId={tbId}
        vid={vid}
        extendsStore={extendsStore}
      >
        <ModalsProvider>{children}</ModalsProvider>
        <ViewMatrixRender />
      </TbViewProvider>
    </DittoProvider>
  )
}

export default TbViewMatrix
